import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Users, Filter, Search, ChevronDown, Phone, Mail, MapPin, 
  Calendar, DollarSign, MoreVertical, ArrowRight, TrendingUp,
  AlertCircle, CheckCircle, Clock, XCircle, Plus, Save, X,
  FileText, Download, Check, StickyNote, UserCog, RefreshCw, Mic,
  Trash2, Archive, Square, CheckSquare, RotateCcw, ArchiveRestore,
  ArrowUpDown, ArrowUp, ArrowDown, Shield, Video, CloudRain, CalendarClock, Bell
} from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import axios from 'axios';
import { toast } from 'sonner';
import AddressAutocomplete from '../ui/AddressAutocomplete';
import AudioNoteRecorder from './AudioNoteRecorder';
import ImageLightbox from '../ui/ImageLightbox';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';

// Lead source options
const DEFAULT_LEAD_SOURCES = [
  { id: 'networking', label: 'Networking', color: 'bg-blue-100 text-blue-700' },
  { id: 'referral', label: 'Referral', color: 'bg-green-100 text-green-700' },
  { id: 'cold_call', label: 'Cold Call', color: 'bg-yellow-100 text-yellow-700' },
  { id: 'door_knocking', label: 'Door Knocking', color: 'bg-orange-100 text-orange-700' },
  { id: 'facebook_ad', label: 'Facebook Ad', color: 'bg-indigo-100 text-indigo-700' },
  { id: 'google_ad', label: 'Google Ad', color: 'bg-red-100 text-red-700' },
  { id: 'website', label: 'Website', color: 'bg-purple-100 text-purple-700' },
  { id: 'search_engine', label: 'Search Engine', color: 'bg-cyan-100 text-cyan-700' },
  { id: 'other', label: 'Other', color: 'bg-gray-100 text-gray-700' },
  { id: 'manual', label: 'Manual Entry', color: 'bg-gray-100 text-gray-600' }
];

// HomeFixer360 Home Service Categories
export const SERVICE_CATEGORIES = [
  { id: 'kitchens_baths', label: 'Kitchens & Baths', color: 'bg-orange-100 text-orange-700', icon: '🍳' },
  { id: 'windows_doors', label: 'Windows & Doors', color: 'bg-blue-100 text-blue-700', icon: '🚪' },
  { id: 'decks_fencing', label: 'Decks & Fencing', color: 'bg-amber-100 text-amber-700', icon: '🪵' },
  { id: 'driveway_repaving', label: 'Driveway Repaving', color: 'bg-slate-100 text-slate-700', icon: '🛣️' },
  { id: 'electrical', label: 'Electrical', color: 'bg-yellow-100 text-yellow-700', icon: '⚡' },
  { id: 'hvac', label: 'HVAC', color: 'bg-sky-100 text-sky-700', icon: '❄️' },
  { id: 'hardscaping', label: 'Hardscaping', color: 'bg-emerald-100 text-emerald-700', icon: '🌳' },
  { id: 'home_renovations', label: 'Home Renovations', color: 'bg-indigo-100 text-indigo-700', icon: '🏠' },
];

export const getServiceCategoryLabel = (id) => SERVICE_CATEGORIES.find(s => s.id === id)?.label || 'Home Renovations';
export const getServiceCategoryColor = (id) => SERVICE_CATEGORIES.find(s => s.id === id)?.color || 'bg-gray-100 text-gray-700';

const SOURCE_COLORS = [
  'bg-blue-100 text-blue-700', 'bg-green-100 text-green-700', 'bg-yellow-100 text-yellow-700',
  'bg-orange-100 text-orange-700', 'bg-indigo-100 text-indigo-700', 'bg-red-100 text-red-700',
  'bg-purple-100 text-purple-700', 'bg-cyan-100 text-cyan-700', 'bg-teal-100 text-teal-700',
  'bg-pink-100 text-pink-700', 'bg-lime-100 text-lime-700', 'bg-amber-100 text-amber-700',
];

const getSourceLabel = (sourceId, sources) => {
  const list = sources?.length ? sources : DEFAULT_LEAD_SOURCES;
  const source = list.find(s => s.id === sourceId || s.key === sourceId);
  return source ? source.label : sourceId || 'Unknown';
};

const getSourceColor = (sourceId, sources) => {
  const list = sources?.length ? sources : DEFAULT_LEAD_SOURCES;
  const idx = list.findIndex(s => s.id === sourceId || s.key === sourceId);
  if (idx >= 0) {
    const s = list[idx];
    return s.color || SOURCE_COLORS[idx % SOURCE_COLORS.length];
  }
  return 'bg-gray-100 text-gray-600';
};

const LeadCRM = () => {
  const navigate = useNavigate();
  const [leads, setLeads] = useState([]);
  const [pipeline, setPipeline] = useState({});
  const [stageCounts, setStageCounts] = useState({});
  const [stats, setStats] = useState(null);
  const [leadSources, setLeadSources] = useState(DEFAULT_LEAD_SOURCES);
  const [stages, setStages] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [viewMode, setViewMode] = useState('pipeline'); // 'pipeline', 'list', 'report', or 'archived'
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedStage, setSelectedStage] = useState(null); // For filtering by stage
  const [selectedLead, setSelectedLead] = useState(null);
  const [showAddLeadModal, setShowAddLeadModal] = useState(false);
  const [pendingStageChanges, setPendingStageChanges] = useState({}); // Track unsaved stage changes
  
  // Admin state - account owners (contractors) are admins of their own data
  const [isAdmin, setIsAdmin] = useState(false);
  const [isAccountOwner, setIsAccountOwner] = useState(false); // True for main contractor account
  const [teamUsers, setTeamUsers] = useState([]);
  const [userFilter, setUserFilter] = useState(''); // '' = own leads, 'all' = all users, or specific user ID
  const [showReassignModal, setShowReassignModal] = useState(null); // lead_id being reassigned
  const [groupReportByUser, setGroupReportByUser] = useState(false);
  
  // Bulk selection state (available to account owners and admins)
  const [selectedLeadIds, setSelectedLeadIds] = useState(new Set());
  const [showBulkDeleteConfirm, setShowBulkDeleteConfirm] = useState(false);
  const [bulkActionType, setBulkActionType] = useState(null); // 'archive', 'delete', or 'restore'
  const [isBulkActionLoading, setIsBulkActionLoading] = useState(false);
  
  // Check if user can use bulk actions (account owner or admin)
  const canUseBulkActions = isAdmin || isAccountOwner;
  
  // Archived leads state
  const [archivedLeads, setArchivedLeads] = useState([]);
  const [isLoadingArchived, setIsLoadingArchived] = useState(false);
  
  // Report state
  const [reportData, setReportData] = useState([]);
  const [reportTotals, setReportTotals] = useState(null);
  const [reportPeriod, setReportPeriod] = useState('month');
  const [customDateRange, setCustomDateRange] = useState({ start: '', end: '' });
  const [isLoadingReport, setIsLoadingReport] = useState(false);
  const [userReports, setUserReports] = useState([]); // For grouped report
  
  // Notes state
  const [editingNotes, setEditingNotes] = useState(null); // lead_id being edited
  const [notesText, setNotesText] = useState('');
  
  // Sort state for report
  const [sortColumn, setSortColumn] = useState(null);
  const [sortDirection, setSortDirection] = useState('asc');

  // Source filter state
  const [sourceFilter, setSourceFilter] = useState('all');

  // Lead files state
  const [leadFiles, setLeadFiles] = useState([]);
  const [isUploadingFile, setIsUploadingFile] = useState(false);
  const [uploadCategory, setUploadCategory] = useState('general');
  const [isEditingLead, setIsEditingLead] = useState(false);
  const [editForm, setEditForm] = useState({});
  const [isSavingLead, setIsSavingLead] = useState(false);
  const fileInputRef = React.useRef(null);
  const [lightboxImages, setLightboxImages] = useState([]);
  const [lightboxIndex, setLightboxIndex] = useState(0);

  // Load stages and check if admin
  useEffect(() => {
    loadStages();
    // Load lead sources from API
    const token = localStorage.getItem('rooftrack_token');
    if (token) {
      axios.get(`${BACKEND_URL}/api/lead-sources`, { headers: { Authorization: `Bearer ${token}` } })
        .then(res => {
          if (res.data?.length > 0) {
            setLeadSources(res.data.map((s, i) => ({
              id: s.key,
              label: s.label,
              color: SOURCE_COLORS[i % SOURCE_COLORS.length],
            })));
          }
        })
        .catch(() => {}); // Fall back to defaults silently
    }
  }, []);

  useEffect(() => {
    loadLeads();
    loadStats();
  }, [searchQuery, selectedStage, userFilter, sourceFilter]);

  // Load report when switching to report view or changing period
  useEffect(() => {
    if (viewMode === 'report') {
      loadReport();
    }
  }, [viewMode, reportPeriod, userFilter, groupReportByUser, sourceFilter]);

  // Load archived leads when switching to archived view
  useEffect(() => {
    if (viewMode === 'archived' && isAdmin) {
      loadArchivedLeads();
    }
  }, [viewMode, userFilter, isAdmin]);

  const loadStages = async () => {
    try {
      const token = localStorage.getItem('rooftrack_token');
      const response = await axios.get(`${BACKEND_URL}/api/crm/stages`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setStages(response.data.stages || []);
      setIsAdmin(response.data.is_admin || false);
      // Account owners (contractors) can manage their own leads
      setIsAccountOwner(response.data.is_account_owner || response.data.role === 'contractor' || true);
      setTeamUsers(response.data.team_users || []);
    } catch (error) {
      console.error('Error loading stages:', error);
    }
  };

  const loadArchivedLeads = async () => {
    setIsLoadingArchived(true);
    try {
      const token = localStorage.getItem('rooftrack_token');
      const params = new URLSearchParams();
      if (userFilter) {
        params.append('user_filter', userFilter);
      }
      
      const response = await axios.get(`${BACKEND_URL}/api/crm/leads/archived?${params}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setArchivedLeads(response.data.archived_leads || []);
    } catch (error) {
      console.error('Error loading archived leads:', error);
      toast.error('Failed to load archived leads');
    } finally {
      setIsLoadingArchived(false);
    }
  };

  const loadReport = async () => {
    setIsLoadingReport(true);
    try {
      const token = localStorage.getItem('rooftrack_token');
      const params = new URLSearchParams({ period: reportPeriod });
      
      if (reportPeriod === 'custom' && customDateRange.start && customDateRange.end) {
        params.append('start_date', customDateRange.start);
        params.append('end_date', customDateRange.end);
      }
      
      // Add admin filters
      if (isAdmin && userFilter) {
        params.append('user_filter', userFilter);
      }
      if (isAdmin && groupReportByUser) {
        params.append('group_by_user', 'true');
      }
      
      // Add source filter
      if (sourceFilter && sourceFilter !== 'all') {
        params.append('source', sourceFilter);
      }
      
      const response = await axios.get(`${BACKEND_URL}/api/crm/report?${params}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setReportData(response.data.report || []);
      setReportTotals(response.data.totals || null);
      setUserReports(response.data.user_reports || []);
    } catch (error) {
      console.error('Error loading report:', error);
      toast.error('Failed to load report');
    } finally {
      setIsLoadingReport(false);
    }
  };

  // Update contractor notes
  const saveContractorNotes = async (leadId, notes) => {
    try {
      const token = localStorage.getItem('rooftrack_token');
      await axios.put(`${BACKEND_URL}/api/crm/leads/${leadId}/notes`, 
        { contractor_notes: notes },
        { headers: { Authorization: `Bearer ${token}` }}
      );
      toast.success('Notes saved');
      setEditingNotes(null);
      loadLeads();
      if (viewMode === 'report') loadReport();
    } catch (error) {
      console.error('Error saving notes:', error);
      toast.error('Failed to save notes');
    }
  };

  // Start editing a lead
  const startEditingLead = (lead) => {
    setEditForm({
      first_name: lead.first_name || '',
      last_name: lead.last_name || '',
      email: lead.email || '',
      phone: lead.phone || '',
      address: lead.address || '',
      agreement_amount: lead.agreement_amount || '',
    });
    setIsEditingLead(true);
  };

  // Save lead edits
  const saveLeadEdits = async () => {
    if (!selectedLead) return;
    setIsSavingLead(true);
    try {
      const token = localStorage.getItem('rooftrack_token');
      const res = await fetch(`${BACKEND_URL}/api/crm/leads/${selectedLead.id}/details`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify(editForm)
      });
      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.detail || 'Failed to update');
      }
      // Update local state
      setSelectedLead({ ...selectedLead, ...editForm });
      setIsEditingLead(false);
      toast.success('Lead updated successfully');
      loadLeads();
    } catch (err) {
      toast.error(err.message || 'Failed to save changes');
    } finally {
      setIsSavingLead(false);
    }
  };

  // Fetch files for selected lead
  const fetchLeadFiles = async (leadId) => {
    try {
      const token = localStorage.getItem('rooftrack_token');
      const res = await fetch(`${BACKEND_URL}/api/crm/leads/${leadId}/files`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setLeadFiles(data.files || []);
      }
    } catch (err) {
      console.error('Failed to fetch files:', err);
    }
  };

  // Upload file to lead
  const handleFileUpload = async (e) => {
    const files = e.target.files;
    if (!files?.length || !selectedLead) return;
    
    setIsUploadingFile(true);
    const token = localStorage.getItem('rooftrack_token');
    
    for (const file of files) {
      const formData = new FormData();
      formData.append('file', file);
      
      try {
        const res = await fetch(
          `${BACKEND_URL}/api/crm/leads/${selectedLead.id}/files?category=${uploadCategory}`,
          {
            method: 'POST',
            headers: { 'Authorization': `Bearer ${token}` },
            body: formData
          }
        );
        if (res.ok) {
          toast.success(`Uploaded ${file.name}`);
        } else {
          const err = await res.json();
          toast.error(err.detail || `Failed to upload ${file.name}`);
        }
      } catch (err) {
        toast.error(`Upload error: ${file.name}`);
      }
    }
    
    setIsUploadingFile(false);
    if (fileInputRef.current) fileInputRef.current.value = '';
    fetchLeadFiles(selectedLead.id);
  };

  // Delete file from lead
  const deleteLeadFile = async (fileId) => {
    if (!selectedLead) return;
    const token = localStorage.getItem('rooftrack_token');
    try {
      const res = await fetch(`${BACKEND_URL}/api/crm/leads/${selectedLead.id}/files/${fileId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        toast.success('File deleted');
        fetchLeadFiles(selectedLead.id);
      }
    } catch (err) {
      toast.error('Failed to delete file');
    }
  };

  // Fetch files when a lead is selected
  useEffect(() => {
    if (selectedLead) {
      fetchLeadFiles(selectedLead.id);
    } else {
      setLeadFiles([]);
    }
  }, [selectedLead?.id]);

  // Sort handler for report columns
  const handleSort = (column) => {
    if (sortColumn === column) {
      setSortDirection(prev => prev === 'asc' ? 'desc' : 'asc');
    } else {
      setSortColumn(column);
      setSortDirection('asc');
    }
  };

  const getSortIcon = (column) => {
    if (sortColumn !== column) return <ArrowUpDown className="w-3 h-3 opacity-40" />;
    return sortDirection === 'asc' 
      ? <ArrowUp className="w-3 h-3" /> 
      : <ArrowDown className="w-3 h-3" />;
  };

  const sortedReportData = [...reportData].sort((a, b) => {
    if (!sortColumn) return 0;
    const dir = sortDirection === 'asc' ? 1 : -1;
    
    switch (sortColumn) {
      case 'name': return dir * (a.name || '').localeCompare(b.name || '');
      case 'address': return dir * (a.address || '').localeCompare(b.address || '');
      case 'estimate': return dir * ((a.estimate_amount || 0) - (b.estimate_amount || 0));
      case 'won': return dir * ((a.won_amount || 0) - (b.won_amount || 0));
      case 'stage': return dir * (a.current_stage || '').localeCompare(b.current_stage || '');
      case 'date': return dir * (new Date(a.created_at || 0) - new Date(b.created_at || 0));
      case 'insurance': return dir * ((a.insurance_claim ? 1 : 0) - (b.insurance_claim ? 1 : 0));
      case 'source': return dir * (a.source || '').localeCompare(b.source || '');
      default: return 0;
    }
  });

  // Export functions
  const exportToCSV = () => {
    if (!reportData.length) return;
    
    const headers = ['Name', 'Address', 'Source', 'Estimate ($)', 'Insurance Claim', 'Contacted', 'Quoted', 'Site Visit', 'Won ($)', 'Lost', 'Notes'];
    const rows = reportData.map(r => [
      r.name,
      r.address,
      getSourceLabel(r.source),
      r.estimate_amount,
      r.insurance_claim ? 'Yes' : 'No',
      r.contacted ? 'Yes' : 'No',
      r.quoted ? 'Yes' : 'No',
      r.site_visit ? 'Yes' : 'No',
      r.won_amount,
      r.lost ? 'Yes' : 'No',
      r.contractor_notes || ''
    ]);
    
    // Add totals row
    rows.push([
      'TOTALS',
      '',
      '',
      reportTotals?.estimate_total || 0,
      reportTotals?.insurance_claim_count || 0,
      reportTotals?.contacted_count || 0,
      reportTotals?.quoted_count || 0,
      reportTotals?.site_visit_count || 0,
      reportTotals?.won_total || 0,
      reportTotals?.lost_count || 0,
      ''
    ]);
    
    const csvContent = [headers, ...rows].map(row => row.map(cell => `"${cell}"`).join(',')).join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `pipeline_report_${reportPeriod}_${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success('CSV exported');
  };

  const exportToPDF = () => {
    // Create a printable version
    const printWindow = window.open('', '_blank');
    printWindow.document.write(`
      <html>
        <head>
          <title>Pipeline Report</title>
          <style>
            body { font-family: Arial, sans-serif; padding: 20px; }
            h1 { color: #1e40af; }
            table { width: 100%; border-collapse: collapse; margin-top: 20px; }
            th, td { border: 1px solid #ddd; padding: 8px; text-align: left; }
            th { background-color: #f3f4f6; font-weight: bold; }
            .check { color: green; }
            .check-blue { color: #3b82f6; }
            .total-row { background-color: #f9fafb; font-weight: bold; }
            .currency { text-align: right; }
          </style>
        </head>
        <body>
          <h1>Pipeline Report - ${reportPeriod.charAt(0).toUpperCase() + reportPeriod.slice(1)}</h1>
          <p>Generated: ${new Date().toLocaleString()}</p>
          <table>
            <thead>
              <tr>
                <th>Name</th>
                <th>Address</th>
                <th>Estimate</th>
                <th>Insurance</th>
                <th>Contacted</th>
                <th>Quoted</th>
                <th>Site Visit</th>
                <th>Won</th>
                <th>Lost</th>
              </tr>
            </thead>
            <tbody>
              ${reportData.map(r => `
                <tr>
                  <td>${r.name}</td>
                  <td>${r.address || '-'}</td>
                  <td class="currency">$${(r.estimate_amount || 0).toLocaleString()}</td>
                  <td>${r.insurance_claim ? '<span class="check-blue">&#10003;</span>' : ''}</td>
                  <td>${r.contacted ? '<span class="check">&#10003;</span>' : ''}</td>
                  <td>${r.quoted ? '<span class="check">&#10003;</span>' : ''}</td>
                  <td>${r.site_visit ? '<span class="check">&#10003;</span>' : ''}</td>
                  <td class="currency">$${(r.won_amount || 0).toLocaleString()}</td>
                  <td>${r.lost ? 'Lost' : ''}</td>
                </tr>
              `).join('')}
              <tr class="total-row">
                <td>TOTALS</td>
                <td></td>
                <td class="currency">$${(reportTotals?.estimate_total || 0).toLocaleString()}</td>
                <td>${reportTotals?.insurance_claim_count || 0}</td>
                <td>${reportTotals?.contacted_count || 0}</td>
                <td>${reportTotals?.quoted_count || 0}</td>
                <td>${reportTotals?.site_visit_count || 0}</td>
                <td class="currency">$${(reportTotals?.won_total || 0).toLocaleString()}</td>
                <td>${reportTotals?.lost_count || 0}</td>
              </tr>
            </tbody>
          </table>
        </body>
      </html>
    `);
    printWindow.document.close();
    printWindow.print();
    toast.success('PDF generated - use browser print dialog to save');
  };

  // Handle clicking a stage to filter
  const handleStageClick = (stageId) => {
    if (selectedStage === stageId) {
      // If already selected, clear the filter
      setSelectedStage(null);
    } else {
      // Set the filter and switch to list view
      setSelectedStage(stageId);
      setViewMode('list');
    }
  };

  // Clear stage filter
  const clearStageFilter = () => {
    setSelectedStage(null);
  };

  const loadLeads = async () => {
    try {
      const token = localStorage.getItem('rooftrack_token');
      const params = new URLSearchParams();
      if (searchQuery) params.append('search', searchQuery);
      if (selectedStage) params.append('stage', selectedStage);
      if (isAdmin && userFilter) params.append('user_filter', userFilter);
      if (sourceFilter && sourceFilter !== 'all') params.append('source', sourceFilter);
      
      const response = await axios.get(`${BACKEND_URL}/api/crm/leads?${params}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setLeads(response.data.leads || []);
      setPipeline(response.data.pipeline || {});
      setStageCounts(response.data.stage_counts || {});
      setStages(response.data.stages || []);
      if (response.data.is_admin !== undefined) {
        setIsAdmin(response.data.is_admin);
      }
    } catch (error) {
      console.error('Error loading leads:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const loadStats = async () => {
    try {
      const token = localStorage.getItem('rooftrack_token');
      const params = new URLSearchParams();
      if (isAdmin && userFilter) params.append('user_filter', userFilter);
      
      const response = await axios.get(`${BACKEND_URL}/api/crm/stats?${params}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setStats(response.data);
    } catch (error) {
      console.error('Error loading stats:', error);
    }
  };

  // Reassign lead to another user (admin only)
  const reassignLead = async (leadId, newOwnerId) => {
    try {
      const token = localStorage.getItem('rooftrack_token');
      const response = await axios.put(`${BACKEND_URL}/api/crm/leads/${leadId}/reassign`, 
        { new_owner_id: newOwnerId },
        { headers: { Authorization: `Bearer ${token}` }}
      );
      toast.success(response.data.message);
      setShowReassignModal(null);
      setSelectedLead(null);
      loadLeads();
    } catch (error) {
      console.error('Error reassigning lead:', error);
      toast.error('Failed to reassign lead');
    }
  };

  // Bulk selection handlers (admin only)
  const toggleLeadSelection = (leadId) => {
    setSelectedLeadIds(prev => {
      const newSet = new Set(prev);
      if (newSet.has(leadId)) {
        newSet.delete(leadId);
      } else {
        newSet.add(leadId);
      }
      return newSet;
    });
  };

  const clearSelection = () => {
    setSelectedLeadIds(new Set());
  };

  // Perform bulk action (archive or delete)
  const performBulkAction = async (action) => {
    if (selectedLeadIds.size === 0) {
      toast.error('No leads selected');
      return;
    }

    setIsBulkActionLoading(true);
    try {
      const token = localStorage.getItem('rooftrack_token');
      
      // Use different endpoint for restore
      const endpoint = action === 'restore' 
        ? `${BACKEND_URL}/api/crm/leads/bulk-restore`
        : `${BACKEND_URL}/api/crm/leads/bulk-action`;
      
      const body = action === 'restore'
        ? { lead_ids: Array.from(selectedLeadIds) }
        : { lead_ids: Array.from(selectedLeadIds), action: action };
      
      const response = await axios.post(endpoint, body, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success(response.data.message);
      setSelectedLeadIds(new Set());
      setShowBulkDeleteConfirm(false);
      setBulkActionType(null);
      
      // Reload appropriate view
      if (action === 'restore') {
        loadArchivedLeads();
      }
      loadLeads();
      loadStats();
    } catch (error) {
      console.error('Error performing bulk action:', error);
      toast.error(error.response?.data?.detail || 'Failed to perform action');
    } finally {
      setIsBulkActionLoading(false);
    }
  };

  // Restore a single lead
  const restoreLead = async (leadId) => {
    try {
      const token = localStorage.getItem('rooftrack_token');
      const response = await axios.post(`${BACKEND_URL}/api/crm/leads/${leadId}/restore`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(response.data.message);
      loadArchivedLeads();
      loadLeads();
      loadStats();
    } catch (error) {
      console.error('Error restoring lead:', error);
      toast.error(error.response?.data?.detail || 'Failed to restore lead');
    }
  };

  const openBulkActionConfirm = (action) => {
    setBulkActionType(action);
    setShowBulkDeleteConfirm(true);
  };

  // Handle stage change in list view - just track the pending change
  const handleStageChange = (leadId, newStage) => {
    setPendingStageChanges(prev => ({
      ...prev,
      [leadId]: newStage
    }));
  };

  // Submit the stage change
  const submitStageChange = async (leadId) => {
    const newStage = pendingStageChanges[leadId];
    if (!newStage) return;

    try {
      const token = localStorage.getItem('rooftrack_token');
      await axios.put(`${BACKEND_URL}/api/crm/leads/${leadId}/stage`, 
        { stage: newStage },
        { headers: { Authorization: `Bearer ${token}` }}
      );
      
      // Clear the pending change
      setPendingStageChanges(prev => {
        const updated = { ...prev };
        delete updated[leadId];
        return updated;
      });
      
      toast.success('Stage updated successfully');
      loadLeads();
    } catch (error) {
      console.error('Error updating lead stage:', error);
      toast.error('Failed to update lead stage');
    }
  };

  // Update stage directly (used in modal)
  const updateLeadStage = async (leadId, newStage) => {
    try {
      const token = localStorage.getItem('rooftrack_token');
      await axios.put(`${BACKEND_URL}/api/crm/leads/${leadId}/stage`, 
        { stage: newStage },
        { headers: { Authorization: `Bearer ${token}` }}
      );
      toast.success('Stage updated successfully');
      loadLeads();
    } catch (error) {
      console.error('Error updating lead stage:', error);
      toast.error('Failed to update lead stage');
    }
  };

  // Update agreement amount for a lead
  const updateAgreementAmount = async (leadId, amount) => {
    try {
      const token = localStorage.getItem('rooftrack_token');
      await axios.put(`${BACKEND_URL}/api/crm/leads/${leadId}/agreement-amount`, 
        { agreement_amount: amount },
        { headers: { Authorization: `Bearer ${token}` }}
      );
      toast.success('Agreement amount saved');
      loadLeads();
    } catch (error) {
      console.error('Error updating agreement amount:', error);
      toast.error('Failed to save agreement amount');
    }
  };

  // Update lead source
  const updateLeadSource = async (leadId, newSource) => {
    try {
      const token = localStorage.getItem('rooftrack_token');
      await axios.put(`${BACKEND_URL}/api/crm/leads/${leadId}/source`, 
        { source: newSource },
        { headers: { Authorization: `Bearer ${token}` }}
      );
      toast.success('Lead source updated');
      loadLeads();
      if (viewMode === 'report') loadReport();
    } catch (error) {
      console.error('Error updating lead source:', error);
      toast.error('Failed to update source');
    }
  };

  // Update check-in date
  const updateCheckInDate = async (leadId, date) => {
    try {
      const token = localStorage.getItem('rooftrack_token');
      await axios.put(`${BACKEND_URL}/api/crm/leads/${leadId}/check-in-date`,
        { check_in_date: date || null },
        { headers: { Authorization: `Bearer ${token}` }}
      );
      toast.success(date ? `Check-in set for ${new Date(date).toLocaleDateString()}` : 'Check-in date cleared');
      loadLeads();
    } catch (error) {
      toast.error('Failed to update check-in date');
    }
  };


  // Update estimated value
  const updateEstimatedValue = async (leadId, amount) => {
    try {
      const token = localStorage.getItem('rooftrack_token');
      await axios.put(`${BACKEND_URL}/api/crm/leads/${leadId}/estimated-value`, 
        { estimated_mid: amount },
        { headers: { Authorization: `Bearer ${token}` }}
      );
      toast.success('Estimated value updated');
      loadLeads();
    } catch (error) {
      console.error('Error updating estimated value:', error);
      toast.error('Failed to update estimate');
    }
  };

  // Start insurance claim for a lead
  const startInsuranceClaim = async (lead) => {
    // Fetch the full estimate to get customer data that may not be on the lead
    let estimateData = {};
    const estimateId = lead.estimate_id || lead.id;
    try {
      const res = await axios.get(`${BACKEND_URL}/api/estimates/${estimateId}`);
      estimateData = res.data || {};
    } catch { /* continue with lead data only */ }
    
    // Helper: return value only if it's real (not empty, not "None")
    const val = (...sources) => {
      for (const s of sources) {
        if (s && s !== 'None' && s !== 'null' && s !== 'undefined') return s;
      }
      return '';
    };
    
    const params = new URLSearchParams();
    params.set('lead_id', lead.id);
    params.set('estimate_id', estimateId);
    
    const firstName = val(lead.first_name, estimateData.customer_first_name, estimateData.first_name);
    const lastName = val(lead.last_name, estimateData.customer_last_name, estimateData.last_name);
    const email = val(lead.email, estimateData.customer_email, estimateData.email);
    const phone = val(lead.phone, estimateData.customer_phone, estimateData.phone);
    const address = val(lead.address, estimateData.address);
    
    if (firstName) params.set('customer_first_name', firstName);
    if (lastName) params.set('customer_last_name', lastName);
    if (email) params.set('customer_email', email);
    if (phone) params.set('customer_phone', phone);
    if (address) params.set('property_address', address);
    if (val(lead.desired_roof_type, lead.roof_type, estimateData.desired_roof_type, estimateData.roof_type)) {
      params.set('roof_type', val(lead.desired_roof_type, lead.roof_type, estimateData.desired_roof_type, estimateData.roof_type));
    }
    
    window.location.href = `/dashboard/claims/new?${params.toString()}`;
  };

  // View the linked insurance claim for a lead
  const viewLinkedClaim = async (lead) => {
    try {
      const token = localStorage.getItem('rooftrack_token');
      const res = await axios.get(`${BACKEND_URL}/api/crm/leads/${lead.id}/linked-data`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.data.claim_id) {
        navigate(`/dashboard/claims/${res.data.claim_id}`);
      } else {
        // No claim found, offer to create one
        toast.info('No claim found for this lead. Starting new claim...');
        startInsuranceClaim(lead);
      }
    } catch (error) {
      console.error('Error finding linked claim:', error);
      toast.error('Could not find linked claim');
    }
  };

  // Update insurance financial fields
  const updateInsuranceFinancials = async (leadId, field, value) => {
    try {
      const token = localStorage.getItem('rooftrack_token');
      const payload = { [field]: value };
      await axios.put(`${BACKEND_URL}/api/crm/leads/${leadId}/insurance-financials`, 
        payload,
        { headers: { Authorization: `Bearer ${token}` }}
      );
      toast.success('Insurance info updated');
      loadLeads();
    } catch (error) {
      console.error('Error updating insurance financials:', error);
      toast.error('Failed to update');
    }
  };

  const getStageIcon = (stageId) => {
    const icons = {
      new: <AlertCircle className="w-4 h-4" />,
      quoted: <DollarSign className="w-4 h-4" />,
      site_visit: <MapPin className="w-4 h-4" />,
      under_contract: <FileText className="w-4 h-4" />,
      insurance: <FileText className="w-4 h-4" />,
      deposit_collected: <CheckCircle className="w-4 h-4" />,
      financing: <DollarSign className="w-4 h-4" />,
      won: <CheckCircle className="w-4 h-4" />,
      lost: <XCircle className="w-4 h-4" />
    };
    return icons[stageId] || <Users className="w-4 h-4" />;
  };

  const formatCurrency = (amount) => {
    if (!amount) return '-';
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      maximumFractionDigits: 0
    }).format(amount);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric'
    });
  };

  const LeadCard = ({ lead }) => {
    const isSelected = selectedLeadIds.has(lead.id);
    
    return (
      <div 
        className={`bg-white rounded-lg border shadow-sm p-4 mb-3 cursor-pointer hover:shadow-md transition-shadow ${isSelected ? 'ring-2 ring-red-500 border-red-300' : ''}`}
        onClick={() => setSelectedLead(lead)}
        data-testid={`lead-card-${lead.id}`}
      >
        <div className="flex justify-between items-start mb-2">
          <div className="flex items-start gap-2">
            {/* Checkbox for bulk selection */}
            {canUseBulkActions && (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  toggleLeadSelection(lead.id);
                }}
                className="mt-0.5 flex-shrink-0"
                data-testid={`select-lead-${lead.id}`}
              >
                {isSelected ? (
                  <CheckSquare className="w-5 h-5 text-red-500" />
                ) : (
                  <Square className="w-5 h-5 text-gray-400 hover:text-gray-600" />
                )}
              </button>
            )}
            <div>
              <h4 className="font-semibold text-gray-900">
                {lead.first_name} {lead.last_name}
              </h4>
              <p className="text-sm text-gray-500 truncate max-w-[200px]">
                {lead.address || lead.email}
              </p>
              {lead.service_category && (
                <span className={`inline-block text-xs font-medium px-2 py-0.5 rounded-full mt-1 ${getServiceCategoryColor(lead.service_category)}`} data-testid={`lead-service-category-${lead.id}`}>
                  {SERVICE_CATEGORIES.find(s => s.id === lead.service_category)?.icon} {getServiceCategoryLabel(lead.service_category)}
                </span>
              )}
              {/* Show owner for admin view */}
              {isAdmin && lead.owner_name && userFilter && (
                <p className="text-xs text-purple-600 mt-1 flex items-center gap-1">
                  <UserCog className="w-3 h-3" />
                  {lead.owner_name}
                </p>
              )}
            </div>
          </div>
          <div className="text-right">
            {lead.agreement_amount ? (
              <div>
                <p className="text-xs text-gray-500">Agreement</p>
                <p className="font-bold text-green-600">
                  {formatCurrency(lead.agreement_amount)}
                </p>
              </div>
            ) : (
              <p className="font-bold text-green-600">
                {formatCurrency(lead.estimated_mid || lead.pricing?.total)}
              </p>
            )}
          </div>
        </div>
        
        {/* Estimated Value Input */}
        <div className="mb-2">
          <div className="flex items-center gap-2">
            <label className="text-xs text-gray-500 whitespace-nowrap">Estimate $:</label>
            <input
              type="number"
              placeholder="Estimated value"
              defaultValue={lead.estimated_mid || lead.pricing?.total || ''}
              onClick={(e) => e.stopPropagation()}
              onBlur={(e) => {
                const value = parseFloat(e.target.value);
                const currentValue = lead.estimated_mid || lead.pricing?.total || 0;
                if (value && value !== currentValue) {
                  updateEstimatedValue(lead.id, value);
                }
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.target.blur();
                }
              }}
              className="w-full px-2 py-1 text-sm border border-gray-200 rounded focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
              data-testid={`lead-estimate-${lead.id}`}
            />
          </div>
        </div>

        {/* Agreement Amount Input */}
        <div className="mb-2">
          <div className="flex items-center gap-2">
            <label className="text-xs text-gray-500 whitespace-nowrap">Agreement $:</label>
            <input
              type="number"
              placeholder="Enter amount"
              defaultValue={lead.agreement_amount || ''}
              onClick={(e) => e.stopPropagation()}
              onBlur={(e) => {
                const value = parseFloat(e.target.value);
                if (value && value !== lead.agreement_amount) {
                  updateAgreementAmount(lead.id, value);
                }
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.target.blur();
                }
              }}
              className="w-full px-2 py-1 text-sm border border-gray-200 rounded focus:ring-1 focus:ring-orange-500 focus:border-orange-500"
            />
          </div>
        </div>

        {/* Lead Source Dropdown */}
        <div className="mb-2">
          <div className="flex items-center gap-2">
            <label className="text-xs text-gray-500 whitespace-nowrap">Source:</label>
            <select
              value={lead.source || 'manual'}
              onChange={(e) => {
                e.stopPropagation();
                updateLeadSource(lead.id, e.target.value);
              }}
              onClick={(e) => e.stopPropagation()}
              className={`w-full px-2 py-1 text-xs border border-gray-200 rounded focus:ring-1 focus:ring-orange-500 focus:border-orange-500 ${getSourceColor(lead.source, leadSources)}`}
              data-testid={`lead-source-${lead.id}`}
            >
              {leadSources.map(source => (
                <option key={source.id} value={source.id}>{source.label}</option>
              ))}
            </select>
          </div>
        </div>

        {/* Insurance Financial Fields - Show for insurance stage or insurance claim leads */}
        {(lead.current_stage === 'insurance' || lead.insurance_claim || lead.financing_interest === 'insurance_claim') && (
          <div className="mb-2 p-2 bg-blue-50 rounded border border-blue-200">
            <div className="flex items-center gap-1 mb-2">
              <Shield className="w-3 h-3 text-blue-600" />
              <span className="text-xs font-semibold text-blue-700">Insurance Financials</span>
            </div>
            <div className="space-y-1.5">
              <div className="flex items-center gap-2">
                <label className="text-xs text-blue-600 whitespace-nowrap w-24">Deposit:</label>
                <input
                  type="number"
                  placeholder="$0"
                  defaultValue={lead.insurance_deposit_collected || ''}
                  onClick={(e) => e.stopPropagation()}
                  onBlur={(e) => {
                    const value = parseFloat(e.target.value) || 0;
                    if (value !== (lead.insurance_deposit_collected || 0)) {
                      updateInsuranceFinancials(lead.id, 'deposit_collected', value);
                    }
                  }}
                  onKeyDown={(e) => { if (e.key === 'Enter') e.target.blur(); }}
                  className="w-full px-2 py-1 text-xs border border-blue-200 rounded focus:ring-1 focus:ring-blue-500 focus:border-blue-500 bg-white"
                  data-testid={`insurance-deposit-${lead.id}`}
                />
              </div>
              <div className="flex items-center gap-2">
                <label className="text-xs text-blue-600 whitespace-nowrap w-24">Deductible:</label>
                <input
                  type="number"
                  placeholder="$0"
                  defaultValue={lead.insurance_deductible_due || ''}
                  onClick={(e) => e.stopPropagation()}
                  onBlur={(e) => {
                    const value = parseFloat(e.target.value) || 0;
                    if (value !== (lead.insurance_deductible_due || 0)) {
                      updateInsuranceFinancials(lead.id, 'deductible_due', value);
                    }
                  }}
                  onKeyDown={(e) => { if (e.key === 'Enter') e.target.blur(); }}
                  className="w-full px-2 py-1 text-xs border border-blue-200 rounded focus:ring-1 focus:ring-blue-500 focus:border-blue-500 bg-white"
                  data-testid={`insurance-deductible-${lead.id}`}
                />
              </div>
              <div className="flex items-center gap-2">
                <label className="text-xs text-blue-600 whitespace-nowrap w-24">Balance Due:</label>
                <input
                  type="number"
                  placeholder="$0"
                  defaultValue={lead.insurance_balance_due || ''}
                  onClick={(e) => e.stopPropagation()}
                  onBlur={(e) => {
                    const value = parseFloat(e.target.value) || 0;
                    if (value !== (lead.insurance_balance_due || 0)) {
                      updateInsuranceFinancials(lead.id, 'balance_due', value);
                    }
                  }}
                  onKeyDown={(e) => { if (e.key === 'Enter') e.target.blur(); }}
                  className="w-full px-2 py-1 text-xs border border-blue-200 rounded focus:ring-1 focus:ring-blue-500 focus:border-blue-500 bg-white"
                  data-testid={`insurance-balance-${lead.id}`}
                />
              </div>
            </div>
          </div>
        )}

        {/* Quick Action Buttons — always visible */}
        <div className="mb-2 flex gap-1.5">
          {/* View/Create Estimate Button */}
          <button
            onClick={async (e) => {
              e.stopPropagation();
              // If lead already has an estimate, navigate to it
              if (lead.estimate_id || lead.type === 'estimate') {
                navigate(`/dashboard/estimates/${lead.estimate_id || lead.id}`);
                return;
              }
              
              const val = (...sources) => {
                for (const s of sources) {
                  if (s && s !== 'None' && s !== 'null' && s !== 'undefined') return s;
                }
                return '';
              };
              
              let address = val(lead.address);
              
              // Check if an estimate already exists for this address
              if (address) {
                try {
                  const token = localStorage.getItem('rooftrack_token');
                  const checkRes = await axios.post(
                    `${BACKEND_URL}/api/estimates/find-by-address`,
                    { address },
                    { headers: { Authorization: `Bearer ${token}` } }
                  );
                  if (checkRes.data.estimate_id) {
                    // Link existing estimate to this lead
                    await axios.put(
                      `${BACKEND_URL}/api/crm/leads/${lead.id}`,
                      { estimate_id: checkRes.data.estimate_id },
                      { headers: { Authorization: `Bearer ${token}` } }
                    ).catch(() => {});
                    toast.info(`Found existing estimate for this address`);
                    navigate(`/dashboard/estimates/${checkRes.data.estimate_id}`);
                    return;
                  }
                } catch { /* no match, proceed to create */ }
              }
              
              // No existing estimate — go to new estimate form
              let firstName = val(lead.first_name);
              let lastName = val(lead.last_name);
              let email = val(lead.email);
              let phone = val(lead.phone);
              
              const params = new URLSearchParams();
              params.set('lead_id', lead.id);
              if (firstName) params.set('first_name', firstName);
              if (lastName) params.set('last_name', lastName);
              if (email) params.set('email', email);
              if (phone) params.set('phone', phone);
              if (address) params.set('address', address);
              if (val(lead.desired_roof_type, lead.roof_type)) params.set('roof_type', val(lead.desired_roof_type, lead.roof_type));
              
              navigate(`/dashboard/estimates/new?${params.toString()}`);
            }}
            className="flex-1 px-2 py-1.5 text-xs bg-green-600 text-white rounded hover:bg-green-700 flex items-center justify-center gap-1 font-medium shadow-sm"
            data-testid={`view-estimate-${lead.id}`}
          >
            <FileText className="w-3 h-3" />
            {lead.estimate_id || lead.type === 'estimate' ? 'View Estimate' : 'New Estimate'}
          </button>
          {/* Start / Open Claim Button — always show, checks for existing claim */}
          <button
            onClick={async (e) => {
              e.stopPropagation();
              try {
                const token = localStorage.getItem('rooftrack_token');
                const res = await axios.get(`${BACKEND_URL}/api/crm/leads/${lead.id}/linked-data`, {
                  headers: { Authorization: `Bearer ${token}` }
                });
                if (res.data.claim_id) {
                  navigate(`/dashboard/claims/${res.data.claim_id}`);
                } else {
                  startInsuranceClaim(lead);
                }
              } catch {
                startInsuranceClaim(lead);
              }
            }}
            className="flex-1 px-2 py-1.5 text-xs bg-orange-500 text-white rounded hover:bg-orange-600 flex items-center justify-center gap-1 font-medium shadow-sm"
            data-testid={`view-claim-${lead.id}`}
          >
            <Shield className="w-3 h-3" />
            Start/Open Claim
          </button>
        </div>
        
        {/* Contractor Notes Preview */}
        {lead.contractor_notes && (
          <div className="mb-2 p-2 bg-yellow-50 rounded border border-yellow-200">
            <div className="flex items-start gap-1">
              <StickyNote className="w-3 h-3 text-yellow-600 mt-0.5 flex-shrink-0" />
              <p className="text-xs text-yellow-800 line-clamp-2">{lead.contractor_notes}</p>
            </div>
          </div>
        )}
        
        {/* Check-in Date */}
        {lead.check_in_date && (
          <div className={`mb-2 p-2 rounded border flex items-center gap-2 ${
            new Date(lead.check_in_date) <= new Date() 
              ? 'bg-red-50 border-red-200' 
              : 'bg-sky-50 border-sky-200'
          }`}>
            <Bell className={`w-3 h-3 flex-shrink-0 ${new Date(lead.check_in_date) <= new Date() ? 'text-red-500' : 'text-sky-500'}`} />
            <span className={`text-xs font-medium ${new Date(lead.check_in_date) <= new Date() ? 'text-red-700' : 'text-sky-700'}`}>
              Check-in: {new Date(lead.check_in_date).toLocaleDateString()}
              {new Date(lead.check_in_date) <= new Date() && ' (overdue)'}
            </span>
          </div>
        )}
        
        <div className="flex items-center gap-4 text-xs text-gray-500">
          <span className="flex items-center gap-1">
            <Calendar className="w-3 h-3" />
            {formatDate(lead.created_at)}
          </span>
          {lead.type === 'estimate' && (
            <span className="px-2 py-0.5 rounded-full bg-blue-100 text-blue-700 text-xs">
              Estimate
            </span>
          )}
          {lead.financing_interest && lead.financing_interest !== 'not_sure' && (
            <span className={`px-2 py-0.5 rounded-full text-xs ${
              lead.financing_interest === 'financing_needed' ? 'bg-purple-100 text-purple-700' :
              lead.financing_interest === 'insurance_claim' ? 'bg-blue-100 text-blue-700' :
              'bg-gray-100 text-gray-700'
            }`}>
              {lead.financing_interest === 'financing_needed' ? 'Financing' :
               lead.financing_interest === 'insurance_claim' ? 'Insurance' :
               lead.financing_interest === 'cash' ? 'Cash' : ''}
            </span>
          )}
          {lead.project_timeline === 'asap' && (
            <span className="px-2 py-0.5 rounded-full bg-red-100 text-red-700 text-xs">
            ASAP
          </span>
        )}
      </div>
    </div>
    );
  };

  // Add Lead Modal Component
  const AddLeadModal = () => {
    const [formData, setFormData] = useState({
      first_name: '',
      last_name: '',
      email: '',
      phone: '',
      address: '',
      estimated_value: '',
      notes: '',
      stage: 'new',
      source: 'manual',
      service_category: 'home_renovations'
    });
    const [isSubmitting, setIsSubmitting] = useState(false);

    const handleSubmit = async (e) => {
      e.preventDefault();
      setIsSubmitting(true);

      try {
        const token = localStorage.getItem('rooftrack_token');
        await axios.post(`${BACKEND_URL}/api/crm/leads`, {
          ...formData,
          estimated_mid: parseFloat(formData.estimated_value) || 0,
          source: formData.source
        }, {
          headers: { Authorization: `Bearer ${token}` }
        });

        toast.success('Lead added successfully');
        setShowAddLeadModal(false);
        loadLeads();
        loadStats();
      } catch (error) {
        console.error('Error adding lead:', error);
        toast.error('Failed to add lead');
      } finally {
        setIsSubmitting(false);
      }
    };

    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
        <div className="bg-white rounded-xl shadow-2xl max-w-lg w-full max-h-[90vh] overflow-y-auto">
          <div className="p-6 border-b flex justify-between items-center">
            <h2 className="text-xl font-bold text-gray-900">Add New Lead</h2>
            <button 
              onClick={() => setShowAddLeadModal(false)}
              className="text-gray-400 hover:text-gray-600"
            >
              <X className="w-6 h-6" />
            </button>
          </div>
          
          <form onSubmit={handleSubmit} className="p-6 space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">First Name *</label>
                <Input
                  required
                  value={formData.first_name}
                  onChange={(e) => setFormData({...formData, first_name: e.target.value})}
                  placeholder="John"
                  data-testid="add-lead-first-name"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Last Name *</label>
                <Input
                  required
                  value={formData.last_name}
                  onChange={(e) => setFormData({...formData, last_name: e.target.value})}
                  placeholder="Smith"
                  data-testid="add-lead-last-name"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Email *</label>
              <Input
                type="email"
                required
                value={formData.email}
                onChange={(e) => setFormData({...formData, email: e.target.value})}
                placeholder="john@example.com"
                data-testid="add-lead-email"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Phone</label>
              <Input
                type="tel"
                value={formData.phone}
                onChange={(e) => setFormData({...formData, phone: e.target.value})}
                placeholder="(555) 123-4567"
                data-testid="add-lead-phone"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Address</label>
              <AddressAutocomplete
                value={formData.address}
                onChange={(val) => setFormData({...formData, address: val})}
                onSelectAddress={(addr) => setFormData({...formData, address: addr.display_name || addr})}
                placeholder="Start typing an address..."
                data-testid="add-lead-address"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Estimated Value ($)</label>
              <Input
                type="number"
                value={formData.estimated_value}
                onChange={(e) => setFormData({...formData, estimated_value: e.target.value})}
                placeholder="15000"
                data-testid="add-lead-value"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Service Category *</label>
              <select
                required
                value={formData.service_category}
                onChange={(e) => setFormData({...formData, service_category: e.target.value})}
                className="w-full border rounded-md px-3 py-2"
                data-testid="add-lead-service-category"
              >
                {SERVICE_CATEGORIES.map(cat => (
                  <option key={cat.id} value={cat.id}>{cat.icon} {cat.label}</option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Initial Stage</label>
              <select
                value={formData.stage}
                onChange={(e) => setFormData({...formData, stage: e.target.value})}
                className="w-full border rounded-md px-3 py-2"
                data-testid="add-lead-stage"
              >
                {stages.map(s => (
                  <option key={s.id} value={s.id}>{s.label}</option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Lead Source</label>
              <select
                value={formData.source}
                onChange={(e) => setFormData({...formData, source: e.target.value})}
                className="w-full border rounded-md px-3 py-2"
                data-testid="add-lead-source"
              >
                {leadSources.map(source => (
                  <option key={source.id} value={source.id}>{source.label}</option>
                ))}
              </select>
            </div>

            {/* Admin: Assign to Team Member */}
            {isAdmin && teamUsers.length > 0 && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1 flex items-center gap-2">
                  <UserCog className="w-4 h-4 text-purple-600" />
                  Assign to Team Member
                </label>
                <select
                  value={formData.assigned_to || ''}
                  onChange={(e) => setFormData({...formData, assigned_to: e.target.value})}
                  className="w-full border rounded-md px-3 py-2"
                  data-testid="add-lead-assign-to"
                >
                  <option value="">Assign to myself</option>
                  {teamUsers.filter(u => !u.is_admin).map(user => (
                    <option key={user.id} value={user.id}>{user.name}</option>
                  ))}
                </select>
              </div>
            )}

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Notes</label>
              <textarea
                value={formData.notes}
                onChange={(e) => setFormData({...formData, notes: e.target.value})}
                placeholder="Additional notes about the lead..."
                className="w-full border rounded-md px-3 py-2 h-24"
                data-testid="add-lead-notes"
              />
            </div>

            <div className="flex gap-3 pt-4">
              <Button
                type="button"
                variant="outline"
                onClick={() => setShowAddLeadModal(false)}
                className="flex-1"
              >
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={isSubmitting}
                className="flex-1 bg-blue-600 hover:bg-blue-700"
                data-testid="add-lead-submit"
              >
                {isSubmitting ? 'Adding...' : 'Add Lead'}
              </Button>
            </div>
          </form>
        </div>
      </div>
    );
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <Users className="w-7 h-7 text-blue-600" />
            Lead Pipeline
            {isAdmin && (
              <span className="ml-2 px-2 py-0.5 rounded-full bg-purple-100 text-purple-700 text-xs font-medium flex items-center gap-1">
                <UserCog className="w-3 h-3" />
                Admin View
              </span>
            )}
          </h1>
          <p className="text-gray-600">
            {isAdmin && userFilter === 'all' 
              ? 'Viewing all team pipelines' 
              : isAdmin && userFilter 
                ? `Viewing ${teamUsers.find(u => u.id === userFilter)?.name || 'user'}'s pipeline`
                : 'Manage your leads from first contact to closed deal'}
          </p>
        </div>
        
        <div className="flex flex-wrap items-center gap-3">
          {/* Admin User Filter */}
          {isAdmin && (
            <select
              value={userFilter}
              onChange={(e) => setUserFilter(e.target.value)}
              className="border rounded-md px-3 py-2 text-sm bg-white"
              data-testid="user-filter-select"
            >
              <option value="">My Pipeline</option>
              <option value="all">All Team Members</option>
              {teamUsers.filter(u => !u.is_admin).map(user => (
                <option key={user.id} value={user.id}>{user.name}</option>
              ))}
            </select>
          )}
          
          <Button
            onClick={() => setShowAddLeadModal(true)}
            className="bg-green-600 hover:bg-green-700"
            data-testid="add-lead-btn"
          >
            <Plus className="w-4 h-4 mr-2" />
            Add Lead
          </Button>
          
          <div className="relative">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <Input
              placeholder="Search leads..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10 w-64"
              data-testid="search-leads"
            />
          </div>

          {/* Source Filter */}
          <select
            value={sourceFilter}
            onChange={(e) => setSourceFilter(e.target.value)}
            className="border rounded-md px-3 py-2 text-sm bg-white"
            data-testid="source-filter-select"
          >
            <option value="all">All Sources</option>
            {leadSources.map(source => (
              <option key={source.id} value={source.id}>{source.label}</option>
            ))}
          </select>
          
          <div className="flex border rounded-lg overflow-hidden">
            <button
              onClick={() => setViewMode('pipeline')}
              className={`px-4 py-2 text-sm ${viewMode === 'pipeline' ? 'bg-blue-600 text-white' : 'bg-white text-gray-700'}`}
              data-testid="view-pipeline-btn"
            >
              Pipeline
            </button>
            <button
              onClick={() => setViewMode('list')}
              className={`px-4 py-2 text-sm ${viewMode === 'list' ? 'bg-blue-600 text-white' : 'bg-white text-gray-700'}`}
              data-testid="view-list-btn"
            >
              List
            </button>
            <button
              onClick={() => setViewMode('report')}
              className={`px-4 py-2 text-sm flex items-center gap-1 ${viewMode === 'report' ? 'bg-blue-600 text-white' : 'bg-white text-gray-700'}`}
              data-testid="view-report-btn"
            >
              <FileText className="w-4 h-4" />
              Report
            </button>
            {isAdmin && (
              <button
                onClick={() => {
                  setViewMode('archived');
                  setSelectedLeadIds(new Set());
                }}
                className={`px-4 py-2 text-sm flex items-center gap-1 ${viewMode === 'archived' ? 'bg-orange-500 text-white' : 'bg-white text-gray-700'}`}
                data-testid="view-archived-btn"
              >
                <Archive className="w-4 h-4" />
                Archived
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Stats Cards */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="bg-white rounded-lg shadow p-4">
            <p className="text-gray-500 text-sm">Total Leads</p>
            <p className="text-2xl font-bold text-gray-900">{stats.total_leads}</p>
          </div>
          <div className="bg-white rounded-lg shadow p-4">
            <p className="text-gray-500 text-sm">Pipeline Value</p>
            <p className="text-2xl font-bold text-green-600">{formatCurrency(stats.pipeline_value)}</p>
          </div>
          <div className="bg-white rounded-lg shadow p-4">
            <p className="text-gray-500 text-sm">Conversion Rate</p>
            <p className="text-2xl font-bold text-blue-600">{stats.conversion_rate}%</p>
          </div>
          <div className="bg-white rounded-lg shadow p-4">
            <p className="text-gray-500 text-sm">Won / Lost</p>
            <p className="text-2xl font-bold">
              <span className="text-green-600">{stats.won_count}</span>
              <span className="text-gray-400 mx-1">/</span>
              <span className="text-red-600">{stats.lost_count}</span>
            </p>
          </div>
        </div>
      )}

      {/* Bulk Action Bar - Show when leads are selected */}
      {canUseBulkActions && selectedLeadIds.size > 0 && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-3" data-testid="bulk-action-bar">
          <div className="flex flex-wrap items-center gap-3">
            <CheckSquare className="w-5 h-5 text-red-600 flex-shrink-0" />
            <span className="font-medium text-red-800">
              {selectedLeadIds.size} lead{selectedLeadIds.size > 1 ? 's' : ''} selected
            </span>
            <button 
              onClick={clearSelection}
              className="text-sm text-red-600 hover:text-red-800 underline"
              data-testid="clear-selection-btn"
            >
              Clear
            </button>
            <span className="text-gray-300">|</span>
            <Button
              onClick={() => openBulkActionConfirm('archive')}
              variant="outline"
              size="sm"
              className="border-orange-400 text-orange-600 hover:bg-orange-50"
              data-testid="archive-selected-btn"
            >
              <Archive className="w-4 h-4 mr-1" />
              Archive
            </Button>
            <Button
              onClick={() => openBulkActionConfirm('delete')}
              variant="outline"
              size="sm"
              className="border-red-500 text-red-600 hover:bg-red-50"
              data-testid="delete-selected-btn"
            >
              <Trash2 className="w-4 h-4 mr-1" />
              Delete
            </Button>
          </div>
        </div>
      )}

      {/* Bulk Action Confirmation Modal */}
      {showBulkDeleteConfirm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl max-w-md w-full p-6">
            <div className="flex items-center gap-3 mb-4">
              {bulkActionType === 'delete' ? (
                <div className="p-3 bg-red-100 rounded-full">
                  <Trash2 className="w-6 h-6 text-red-600" />
                </div>
              ) : bulkActionType === 'restore' ? (
                <div className="p-3 bg-green-100 rounded-full">
                  <RotateCcw className="w-6 h-6 text-green-600" />
                </div>
              ) : (
                <div className="p-3 bg-orange-100 rounded-full">
                  <Archive className="w-6 h-6 text-orange-600" />
                </div>
              )}
              <div>
                <h3 className="text-lg font-bold text-gray-900">
                  {bulkActionType === 'delete' ? 'Delete Leads Permanently?' : 
                   bulkActionType === 'restore' ? 'Restore Leads?' : 'Archive Leads?'}
                </h3>
                <p className="text-sm text-gray-500">
                  {selectedLeadIds.size} lead{selectedLeadIds.size > 1 ? 's' : ''} selected
                </p>
              </div>
            </div>
            
            <p className="text-gray-600 mb-6">
              {bulkActionType === 'delete' 
                ? 'This action cannot be undone. The selected leads and all their data will be permanently removed.'
                : bulkActionType === 'restore'
                ? 'The selected leads will be restored to the active pipeline.'
                : 'Archived leads will be hidden from the pipeline but can be recovered later.'}
            </p>
            
            <div className="flex gap-3">
              <Button
                variant="outline"
                onClick={() => {
                  setShowBulkDeleteConfirm(false);
                  setBulkActionType(null);
                }}
                className="flex-1"
                disabled={isBulkActionLoading}
              >
                Cancel
              </Button>
              <Button
                onClick={() => performBulkAction(bulkActionType)}
                className={`flex-1 ${
                  bulkActionType === 'delete' ? 'bg-red-600 hover:bg-red-700' : 
                  bulkActionType === 'restore' ? 'bg-green-600 hover:bg-green-700' :
                  'bg-orange-500 hover:bg-orange-600'
                }`}
                disabled={isBulkActionLoading}
                data-testid="confirm-bulk-action-btn"
              >
                {isBulkActionLoading ? 'Processing...' : 
                  (bulkActionType === 'delete' ? 'Delete Forever' : 
                   bulkActionType === 'restore' ? 'Restore' : 'Archive')}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Stage Filter Tabs - Only show for pipeline/list views */}
      {viewMode !== 'archived' && (
      <div className="flex flex-wrap gap-2 items-center">
        <span className="text-sm text-gray-500 mr-2">Filter by stage:</span>
        <button
          onClick={() => setSelectedStage(null)}
          className={`px-3 py-1.5 rounded-full text-sm font-medium transition-colors ${
            !selectedStage 
              ? 'bg-blue-600 text-white' 
              : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
          }`}
          data-testid="filter-all"
        >
          All ({leads.length})
        </button>
        {stages.map((stage) => (
          <button
            key={stage.id}
            onClick={() => handleStageClick(stage.id)}
            className={`px-3 py-1.5 rounded-full text-sm font-medium transition-colors flex items-center gap-1.5 ${
              selectedStage === stage.id
                ? 'text-white'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
            style={selectedStage === stage.id ? { backgroundColor: stage.color } : {}}
            data-testid={`filter-${stage.id}`}
          >
            {getStageIcon(stage.id)}
            {stage.label} ({stageCounts[stage.id] || 0})
          </button>
        ))}
      </div>
      )}

      {/* Archived Leads View */}
      {viewMode === 'archived' && isAdmin && (
        <div className="space-y-4">
          {/* Archived Header */}
          <div className="bg-orange-50 border border-orange-200 rounded-lg p-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <Archive className="w-6 h-6 text-orange-600" />
                <div>
                  <h2 className="font-semibold text-orange-800">Archived Leads</h2>
                  <p className="text-sm text-orange-600">
                    {archivedLeads.length} archived lead{archivedLeads.length !== 1 ? 's' : ''}
                  </p>
                </div>
              </div>
              {selectedLeadIds.size > 0 && (
                <div className="flex items-center gap-3">
                  <span className="text-sm text-orange-700">
                    {selectedLeadIds.size} selected
                  </span>
                  <Button
                    size="sm"
                    onClick={() => openBulkActionConfirm('restore')}
                    className="bg-green-600 hover:bg-green-700"
                    data-testid="bulk-restore-btn"
                  >
                    <RotateCcw className="w-4 h-4 mr-1" />
                    Restore Selected
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => openBulkActionConfirm('delete')}
                    className="border-red-500 text-red-600 hover:bg-red-50"
                    data-testid="bulk-delete-archived-btn"
                  >
                    <Trash2 className="w-4 h-4 mr-1" />
                    Delete Forever
                  </Button>
                  <button
                    onClick={clearSelection}
                    className="text-sm text-orange-600 hover:text-orange-800 underline"
                  >
                    Clear
                  </button>
                </div>
              )}
            </div>
          </div>

          {/* Archived Leads Table */}
          {isLoadingArchived ? (
            <div className="bg-white rounded-lg shadow p-12 text-center text-gray-500">
              Loading archived leads...
            </div>
          ) : archivedLeads.length === 0 ? (
            <div className="bg-white rounded-lg shadow p-12 text-center">
              <Archive className="w-12 h-12 mx-auto mb-4 text-gray-300" />
              <p className="text-gray-500">No archived leads</p>
              <p className="text-sm text-gray-400 mt-2">
                Leads you archive will appear here. You can restore them anytime.
              </p>
            </div>
          ) : (
            <div className="bg-white rounded-lg shadow overflow-hidden">
              <table className="w-full">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 w-10"></th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500">Contact</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500">Address</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500">Owner</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500">Value</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500">Archived</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {archivedLeads.map((lead) => {
                    const isSelected = selectedLeadIds.has(lead.id);
                    return (
                      <tr key={lead.id} className={`hover:bg-gray-50 ${isSelected ? 'bg-orange-50' : ''}`} data-testid={`archived-row-${lead.id}`}>
                        <td className="px-4 py-3">
                          <button
                            onClick={() => toggleLeadSelection(lead.id)}
                            data-testid={`select-archived-${lead.id}`}
                          >
                            {isSelected ? (
                              <CheckSquare className="w-5 h-5 text-orange-500" />
                            ) : (
                              <Square className="w-5 h-5 text-gray-400 hover:text-gray-600" />
                            )}
                          </button>
                        </td>
                        <td className="px-4 py-3">
                          <div>
                            <p className="font-medium text-gray-900">{lead.first_name} {lead.last_name}</p>
                            <p className="text-sm text-gray-500">{lead.email}</p>
                          </div>
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-600 max-w-[200px] truncate">
                          {lead.address || '-'}
                        </td>
                        <td className="px-4 py-3 text-sm text-purple-600">
                          {lead.owner_name || '-'}
                        </td>
                        <td className="px-4 py-3 font-semibold text-green-600">
                          {formatCurrency(lead.estimated_mid || lead.pricing?.total)}
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-500">
                          {lead.archived_at ? new Date(lead.archived_at).toLocaleDateString() : '-'}
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-2">
                            <Button
                              size="sm"
                              onClick={() => restoreLead(lead.id)}
                              className="bg-green-600 hover:bg-green-700"
                              data-testid={`restore-lead-${lead.id}`}
                            >
                              <RotateCcw className="w-4 h-4 mr-1" />
                              Restore
                            </Button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* Pipeline View */}
      {viewMode === 'pipeline' && (
        <div className="flex gap-4 overflow-x-auto pb-4">
          {stages.map((stage) => (
            <div 
              key={stage.id}
              className="flex-shrink-0 w-72 bg-gray-50 rounded-lg"
            >
              {/* Stage Header - Clickable */}
              <div 
                onClick={() => handleStageClick(stage.id)}
                className="p-3 border-b flex items-center justify-between cursor-pointer hover:bg-gray-100 transition-colors"
                style={{ borderLeftColor: stage.color, borderLeftWidth: 4 }}
                data-testid={`stage-header-${stage.id}`}
              >
                <div className="flex items-center gap-2">
                  {getStageIcon(stage.id)}
                  <span className="font-semibold text-gray-900">{stage.label}</span>
                </div>
                <span className="bg-gray-200 text-gray-700 px-2 py-0.5 rounded-full text-sm font-medium">
                  {stageCounts[stage.id] || 0}
                </span>
              </div>
              
              {/* Stage Content */}
              <div className="p-3 max-h-[500px] overflow-y-auto">
                {(pipeline[stage.id] || []).length === 0 ? (
                  <p className="text-gray-400 text-sm text-center py-4">No leads</p>
                ) : (
                  (pipeline[stage.id] || []).map((lead) => (
                    <LeadCard key={lead.id} lead={lead} />
                  ))
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* List View */}
      {viewMode === 'list' && (
        <div className="bg-white rounded-lg shadow overflow-hidden">
          {/* List Header with filter info */}
          {selectedStage && (
            <div className="px-4 py-3 bg-gray-50 border-b flex items-center justify-between">
              <div className="flex items-center gap-2">
                <span className="text-gray-600">Showing:</span>
                <span 
                  className="px-3 py-1 rounded-full text-white text-sm font-medium"
                  style={{ backgroundColor: stages.find(s => s.id === selectedStage)?.color || '#6B7280' }}
                >
                  {stages.find(s => s.id === selectedStage)?.label || selectedStage}
                </span>
                <span className="text-gray-500">
                  ({(pipeline[selectedStage] || []).length} leads)
                </span>
              </div>
              <Button
                size="sm"
                variant="outline"
                onClick={clearStageFilter}
                data-testid="clear-filter-btn"
              >
                <X className="w-4 h-4 mr-1" />
                Clear Filter
              </Button>
            </div>
          )}
          
          <table className="w-full">
            <thead className="bg-gray-50">
              <tr>
                {canUseBulkActions && <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 w-10"></th>}
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500">Contact</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500">Address</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500">Source</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500">Type</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500">Stage</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500">Value</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500">Date</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y">
              {(selectedStage ? (pipeline[selectedStage] || []) : leads).map((lead) => {
                const currentStage = pendingStageChanges[lead.id] || lead.stage || 'new';
                const hasChanges = pendingStageChanges[lead.id] && pendingStageChanges[lead.id] !== lead.stage;
                const isSelected = selectedLeadIds.has(lead.id);
                
                return (
                  <tr key={lead.id} className={`hover:bg-gray-50 ${isSelected ? 'bg-red-50' : ''}`} data-testid={`lead-row-${lead.id}`}>
                    {canUseBulkActions && (
                      <td className="px-4 py-3">
                        <button
                          onClick={() => toggleLeadSelection(lead.id)}
                          data-testid={`select-list-lead-${lead.id}`}
                        >
                          {isSelected ? (
                            <CheckSquare className="w-5 h-5 text-red-500" />
                          ) : (
                            <Square className="w-5 h-5 text-gray-400 hover:text-gray-600" />
                          )}
                        </button>
                      </td>
                    )}
                    <td className="px-4 py-3">
                      <div>
                        <p className="font-medium text-gray-900">{lead.first_name} {lead.last_name}</p>
                        <p className="text-sm text-gray-500">{lead.email}</p>
                      </div>
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-600 max-w-[200px] truncate">
                      {lead.address || '-'}
                    </td>
                    <td className="px-4 py-3">
                      <select
                        value={lead.source || ''}
                        onChange={(e) => updateLeadSource(lead.id, e.target.value)}
                        className={`text-xs border border-gray-200 rounded px-2 py-1 ${getSourceColor(lead.source, leadSources)}`}
                        data-testid={`list-source-select-${lead.id}`}
                      >
                        <option value="">No Source</option>
                        {leadSources.map(source => (
                          <option key={source.id} value={source.id}>{source.label}</option>
                        ))}
                      </select>
                    </td>
                    <td className="px-4 py-3">
                      <span className={`px-2 py-1 rounded-full text-xs ${
                        lead.type === 'estimate' ? 'bg-blue-100 text-blue-700' : 'bg-gray-100 text-gray-700'
                      }`}>
                        {lead.type === 'estimate' ? 'Estimate' : 'Lead'}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        <select
                          value={currentStage}
                          onChange={(e) => handleStageChange(lead.id, e.target.value)}
                          className={`text-sm border rounded px-2 py-1 ${hasChanges ? 'border-orange-400 bg-orange-50' : ''}`}
                          data-testid={`stage-select-${lead.id}`}
                        >
                          {stages.map(s => (
                            <option key={s.id} value={s.id}>{s.label}</option>
                          ))}
                        </select>
                        {hasChanges && (
                          <Button
                            size="sm"
                            onClick={() => submitStageChange(lead.id)}
                            className="bg-green-600 hover:bg-green-700 px-2 py-1"
                            data-testid={`save-stage-${lead.id}`}
                          >
                            <Save className="w-4 h-4" />
                          </Button>
                        )}
                      </div>
                    </td>
                    <td className="px-4 py-3 font-semibold text-green-600">
                      {formatCurrency(lead.estimated_mid || lead.pricing?.total)}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-500">
                      {formatDate(lead.created_at)}
                    </td>
                    <td className="px-4 py-3">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => setSelectedLead(lead)}
                        data-testid={`view-lead-${lead.id}`}
                      >
                        View
                      </Button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          
          {(selectedStage ? (pipeline[selectedStage] || []) : leads).length === 0 && (
            <div className="text-center py-12 text-gray-500">
              {selectedStage ? (
                <>
                  <p>No leads in "{stages.find(s => s.id === selectedStage)?.label}" stage.</p>
                  <p className="text-sm mt-2">
                    <button 
                      onClick={clearStageFilter}
                      className="text-blue-600 hover:underline"
                    >
                      View all leads
                    </button>
                  </p>
                </>
              ) : (
                <>
                  <p>No leads found.</p>
                  <p className="text-sm mt-2">Click "Add Lead" to add your first lead to the pipeline.</p>
                </>
              )}
            </div>
          )}
        </div>
      )}

      {/* Report View */}
      {viewMode === 'report' && (
        <div className="bg-white rounded-lg shadow overflow-hidden">
          {/* Report Header */}
          <div className="px-4 py-4 bg-gray-50 border-b flex flex-wrap items-center justify-between gap-4">
            <div className="flex items-center gap-4">
              <div className="flex items-center gap-2">
                <span className="text-sm text-gray-600">Period:</span>
                <select
                  value={reportPeriod}
                  onChange={(e) => setReportPeriod(e.target.value)}
                  className="border rounded-md px-3 py-1.5 text-sm"
                  data-testid="report-period-select"
                >
                  <option value="week">This Week</option>
                  <option value="month">This Month</option>
                  <option value="quarter">This Quarter</option>
                  <option value="year">This Year</option>
                  <option value="custom">Custom Range</option>
                </select>
              </div>
              
              {reportPeriod === 'custom' && (
                <div className="flex items-center gap-2">
                  <Input
                    type="date"
                    value={customDateRange.start}
                    onChange={(e) => setCustomDateRange(prev => ({ ...prev, start: e.target.value }))}
                    className="w-36 text-sm"
                    data-testid="report-start-date"
                  />
                  <span className="text-gray-500">to</span>
                  <Input
                    type="date"
                    value={customDateRange.end}
                    onChange={(e) => setCustomDateRange(prev => ({ ...prev, end: e.target.value }))}
                    className="w-36 text-sm"
                    data-testid="report-end-date"
                  />
                  <Button size="sm" onClick={loadReport} data-testid="apply-custom-dates">
                    Apply
                  </Button>
                </div>
              )}
              
              {/* Admin: Group by User toggle */}
              {isAdmin && userFilter === 'all' && (
                <label className="flex items-center gap-2 text-sm text-gray-600 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={groupReportByUser}
                    onChange={(e) => setGroupReportByUser(e.target.checked)}
                    className="rounded text-purple-600"
                    data-testid="group-by-user-toggle"
                  />
                  <span>Group by User</span>
                </label>
              )}
            </div>
            
            <div className="flex items-center gap-2">
              <Button
                size="sm"
                variant="outline"
                onClick={exportToCSV}
                disabled={!reportData.length}
                data-testid="export-csv-btn"
              >
                <Download className="w-4 h-4 mr-1" />
                CSV
              </Button>
              <Button
                size="sm"
                variant="outline"
                onClick={exportToPDF}
                disabled={!reportData.length}
                data-testid="export-pdf-btn"
              >
                <Download className="w-4 h-4 mr-1" />
                PDF
              </Button>
            </div>
          </div>
          
          {/* Report Table */}
          {isLoadingReport ? (
            <div className="p-12 text-center text-gray-500">Loading report...</div>
          ) : groupReportByUser && userReports.length > 0 ? (
            /* Grouped by User View */
            <div className="divide-y">
              {userReports.map((userReport) => (
                <div key={userReport.user_id} className="py-4">
                  <div className="px-4 py-2 bg-purple-50 flex items-center justify-between">
                    <h3 className="font-semibold text-purple-700 flex items-center gap-2">
                      <UserCog className="w-4 h-4" />
                      {userReport.user_name}
                    </h3>
                    <div className="flex gap-4 text-sm text-gray-600">
                      <span>Leads: {userReport.report.length}</span>
                      <span>Pipeline: {formatCurrency(userReport.totals.estimate_total)}</span>
                      <span className="text-blue-600">Insurance: {userReport.totals.insurance_claim_count}</span>
                      <span className="text-green-600">Won: {formatCurrency(userReport.totals.won_total)}</span>
                    </div>
                  </div>
                  <table className="w-full">
                    <thead className="bg-gray-100">
                      <tr>
                        <th className="px-4 py-2 text-left text-xs font-semibold text-gray-600">Name</th>
                        <th className="px-4 py-2 text-left text-xs font-semibold text-gray-600">Address</th>
                        <th className="px-4 py-2 text-right text-xs font-semibold text-gray-600">Estimate $</th>
                        <th className="px-4 py-2 text-center text-xs font-semibold text-gray-600">Insurance</th>
                        <th className="px-4 py-2 text-center text-xs font-semibold text-gray-600">Contacted</th>
                        <th className="px-4 py-2 text-center text-xs font-semibold text-gray-600">Quoted</th>
                        <th className="px-4 py-2 text-center text-xs font-semibold text-gray-600">Site Visit</th>
                        <th className="px-4 py-2 text-right text-xs font-semibold text-gray-600">Won $</th>
                        <th className="px-4 py-2 text-center text-xs font-semibold text-gray-600">Lost</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y">
                      {userReport.report.map((row) => (
                        <tr key={row.id} className="hover:bg-gray-50">
                          <td className="px-4 py-2 font-medium text-gray-900">{row.name}</td>
                          <td className="px-4 py-2 text-sm text-gray-600 max-w-[150px] truncate">{row.address || '-'}</td>
                          <td className="px-4 py-2 text-right font-semibold text-gray-700">{formatCurrency(row.estimate_amount)}</td>
                          <td className="px-4 py-2 text-center">{row.insurance_claim && <Check className="w-4 h-4 text-blue-500 mx-auto" />}</td>
                          <td className="px-4 py-2 text-center">{row.contacted && <Check className="w-4 h-4 text-green-500 mx-auto" />}</td>
                          <td className="px-4 py-2 text-center">{row.quoted && <Check className="w-4 h-4 text-green-500 mx-auto" />}</td>
                          <td className="px-4 py-2 text-center">{row.site_visit && <Check className="w-4 h-4 text-green-500 mx-auto" />}</td>
                          <td className="px-4 py-2 text-right font-semibold text-green-600">{row.won_amount > 0 ? formatCurrency(row.won_amount) : '-'}</td>
                          <td className="px-4 py-2 text-center">{row.lost && <XCircle className="w-4 h-4 text-red-500 mx-auto" />}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ))}
              
              {/* Combined Totals */}
              {reportTotals && (
                <div className="px-4 py-4 bg-gray-100">
                  <h3 className="font-bold text-gray-900 mb-2">Combined Team Totals</h3>
                  <div className="grid grid-cols-4 gap-4">
                    <div>
                      <p className="text-sm text-gray-500">Total Pipeline Value</p>
                      <p className="text-xl font-bold text-gray-900">{formatCurrency(reportTotals.estimate_total)}</p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-500">Total Won</p>
                      <p className="text-xl font-bold text-green-600">{formatCurrency(reportTotals.won_total)}</p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-500">Contacted</p>
                      <p className="text-xl font-bold text-gray-900">{reportTotals.contacted_count}</p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-500">Lost</p>
                      <p className="text-xl font-bold text-red-600">{reportTotals.lost_count}</p>
                    </div>
                  </div>
                </div>
              )}
            </div>
          ) : (
            <div className="overflow-x-auto print-area">
              <table className="w-full" data-testid="pipeline-report-table">
                <thead className="bg-gray-100">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 cursor-pointer select-none hover:bg-gray-200" onClick={() => handleSort('name')} data-testid="sort-name">
                      <span className="flex items-center gap-1">Name {getSortIcon('name')}</span>
                    </th>
                    {isAdmin && userFilter === 'all' && (
                      <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600">Owner</th>
                    )}
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 cursor-pointer select-none hover:bg-gray-200" onClick={() => handleSort('address')} data-testid="sort-address">
                      <span className="flex items-center gap-1">Address {getSortIcon('address')}</span>
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 cursor-pointer select-none hover:bg-gray-200" onClick={() => handleSort('source')} data-testid="sort-source">
                      <span className="flex items-center gap-1">Source {getSortIcon('source')}</span>
                    </th>
                    <th className="px-4 py-3 text-right text-xs font-semibold text-gray-600 cursor-pointer select-none hover:bg-gray-200" onClick={() => handleSort('estimate')} data-testid="sort-estimate">
                      <span className="flex items-center justify-end gap-1">Estimate $ {getSortIcon('estimate')}</span>
                    </th>
                    <th className="px-4 py-3 text-center text-xs font-semibold text-gray-600 cursor-pointer select-none hover:bg-gray-200" onClick={() => handleSort('insurance')} data-testid="sort-insurance">
                      <span className="flex items-center justify-center gap-1">Insurance {getSortIcon('insurance')}</span>
                    </th>
                    <th className="px-4 py-3 text-center text-xs font-semibold text-gray-600">Contacted</th>
                    <th className="px-4 py-3 text-center text-xs font-semibold text-gray-600">Quoted</th>
                    <th className="px-4 py-3 text-center text-xs font-semibold text-gray-600">Site Visit</th>
                    <th className="px-4 py-3 text-right text-xs font-semibold text-gray-600 cursor-pointer select-none hover:bg-gray-200" onClick={() => handleSort('won')} data-testid="sort-won">
                      <span className="flex items-center justify-end gap-1">Won $ {getSortIcon('won')}</span>
                    </th>
                    <th className="px-4 py-3 text-center text-xs font-semibold text-gray-600">Lost</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600">Notes</th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {sortedReportData.map((row) => (
                    <tr key={row.id} className="hover:bg-gray-50" data-testid={`report-row-${row.id}`}>
                      <td className="px-4 py-3 font-medium text-gray-900">{row.name}</td>
                      {isAdmin && userFilter === 'all' && (
                        <td className="px-4 py-3 text-sm text-purple-600">{row.owner_name || '-'}</td>
                      )}
                      <td className="px-4 py-3 text-sm text-gray-600 max-w-[200px] truncate">{row.address || '-'}</td>
                      <td className="px-4 py-3">
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${getSourceColor(row.source)}`}>
                          {getSourceLabel(row.source)}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-right font-semibold text-gray-700">
                        {formatCurrency(row.estimate_amount)}
                      </td>
                      <td className="px-4 py-3 text-center">
                        {row.insurance_claim && <Check className="w-5 h-5 text-blue-500 mx-auto" />}
                      </td>
                      <td className="px-4 py-3 text-center">
                        {row.contacted && <Check className="w-5 h-5 text-green-500 mx-auto" />}
                      </td>
                      <td className="px-4 py-3 text-center">
                        {row.quoted && <Check className="w-5 h-5 text-green-500 mx-auto" />}
                      </td>
                      <td className="px-4 py-3 text-center">
                        {row.site_visit && <Check className="w-5 h-5 text-green-500 mx-auto" />}
                      </td>
                      <td className="px-4 py-3 text-right font-semibold text-green-600">
                        {row.won_amount > 0 ? formatCurrency(row.won_amount) : '-'}
                      </td>
                      <td className="px-4 py-3 text-center">
                        {row.lost && <XCircle className="w-5 h-5 text-red-500 mx-auto" />}
                      </td>
                      <td className="px-4 py-3">
                        {editingNotes === row.id ? (
                          <div className="flex items-center gap-1">
                            <Input
                              value={notesText}
                              onChange={(e) => setNotesText(e.target.value)}
                              className="text-xs h-8 w-32"
                              placeholder="Add note..."
                              data-testid={`notes-input-${row.id}`}
                            />
                            <Button
                              size="sm"
                              className="h-8 px-2"
                              onClick={() => saveContractorNotes(row.id, notesText)}
                            >
                              <Save className="w-3 h-3" />
                            </Button>
                            <Button
                              size="sm"
                              variant="outline"
                              className="h-8 px-2"
                              onClick={() => setEditingNotes(null)}
                            >
                              <X className="w-3 h-3" />
                            </Button>
                          </div>
                        ) : (
                          <button
                            className="text-xs text-gray-500 hover:text-blue-600 flex items-center gap-1 max-w-[120px] truncate"
                            onClick={() => {
                              setEditingNotes(row.id);
                              setNotesText(row.contractor_notes || '');
                            }}
                            data-testid={`edit-notes-${row.id}`}
                          >
                            <StickyNote className="w-3 h-3 flex-shrink-0" />
                            {row.contractor_notes || 'Add note'}
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                  
                  {/* Totals Row */}
                  {reportTotals && (
                    <tr className="bg-gray-100 font-bold" data-testid="report-totals-row">
                      <td className="px-4 py-3 text-gray-900">TOTALS</td>
                      {isAdmin && userFilter === 'all' && <td className="px-4 py-3"></td>}
                      <td className="px-4 py-3"></td>
                      <td className="px-4 py-3"></td>
                      <td className="px-4 py-3 text-right text-gray-900">
                        {formatCurrency(reportTotals.estimate_total)}
                      </td>
                      <td className="px-4 py-3 text-center text-blue-600">{reportTotals.insurance_claim_count}</td>
                      <td className="px-4 py-3 text-center text-gray-700">{reportTotals.contacted_count}</td>
                      <td className="px-4 py-3 text-center text-gray-700">{reportTotals.quoted_count}</td>
                      <td className="px-4 py-3 text-center text-gray-700">{reportTotals.site_visit_count}</td>
                      <td className="px-4 py-3 text-right text-green-600">
                        {formatCurrency(reportTotals.won_total)}
                      </td>
                      <td className="px-4 py-3 text-center text-red-600">{reportTotals.lost_count}</td>
                      <td className="px-4 py-3"></td>
                    </tr>
                  )}
                </tbody>
              </table>
              
              {reportData.length === 0 && (
                <div className="text-center py-12 text-gray-500">
                  <FileText className="w-12 h-12 mx-auto mb-4 text-gray-300" />
                  <p>No leads found for this period.</p>
                  <p className="text-sm mt-2">Try selecting a different date range.</p>
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* Lead Detail Modal */}
      {selectedLead && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b flex justify-between items-start">
              <div>
                {isEditingLead ? (
                  <div className="flex gap-2 mb-1">
                    <input
                      className="text-xl font-bold border rounded px-2 py-1 w-40"
                      value={editForm.first_name}
                      onChange={e => setEditForm({...editForm, first_name: e.target.value})}
                      placeholder="First Name"
                      data-testid="edit-first-name"
                    />
                    <input
                      className="text-xl font-bold border rounded px-2 py-1 w-40"
                      value={editForm.last_name}
                      onChange={e => setEditForm({...editForm, last_name: e.target.value})}
                      placeholder="Last Name"
                      data-testid="edit-last-name"
                    />
                  </div>
                ) : (
                  <h2 className="text-2xl font-bold text-gray-900">
                    {selectedLead.first_name} {selectedLead.last_name}
                  </h2>
                )}
                {isEditingLead ? (
                  <input
                    className="text-sm text-gray-500 border rounded px-2 py-1 w-full mt-1"
                    value={editForm.address}
                    onChange={e => setEditForm({...editForm, address: e.target.value})}
                    placeholder="Address"
                    data-testid="edit-address"
                  />
                ) : (
                  <p className="text-gray-500">{selectedLead.address}</p>
                )}
                {selectedLead.type === 'estimate' && (
                  <span className="inline-block mt-2 px-2 py-1 rounded-full bg-blue-100 text-blue-700 text-xs">
                    From Estimate
                  </span>
                )}
              </div>
              <div className="flex items-center gap-2">
                {isEditingLead ? (
                  <>
                    <Button
                      size="sm"
                      onClick={saveLeadEdits}
                      disabled={isSavingLead}
                      data-testid="save-lead-btn"
                    >
                      <Save className="w-4 h-4 mr-1" />
                      {isSavingLead ? 'Saving...' : 'Save'}
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => setIsEditingLead(false)}
                    >
                      Cancel
                    </Button>
                  </>
                ) : (
                  <>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => startEditingLead(selectedLead)}
                      data-testid="edit-lead-btn"
                    >
                      <FileText className="w-4 h-4 mr-1" />
                      Edit
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      className="text-orange-600 border-orange-300 hover:bg-orange-50"
                      onClick={() => {
                        const params = new URLSearchParams({
                          lead_id: selectedLead.id,
                          lead_name: `${selectedLead.first_name} ${selectedLead.last_name}`,
                          lead_address: selectedLead.address || ''
                        });
                        navigate(`/dashboard/field-cam?${params.toString()}`);
                      }}
                      data-testid="fieldcam-btn"
                    >
                      <Video className="w-4 h-4 mr-1" />
                      FieldCam
                    </Button>
                  </>
                )}
                <button 
                  onClick={() => { setSelectedLead(null); setIsEditingLead(false); }}
                  className="text-gray-400 hover:text-gray-600 text-2xl"
                  data-testid="close-lead-modal"
                >
                  ×
                </button>
              </div>
            </div>
            
            <div className="p-6 space-y-6">
              {/* Contact Info */}
              {isEditingLead ? (
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="text-xs text-gray-500 block mb-1">Email</label>
                    <input
                      className="border rounded px-3 py-2 w-full text-sm"
                      value={editForm.email}
                      onChange={e => setEditForm({...editForm, email: e.target.value})}
                      placeholder="Email"
                      data-testid="edit-email"
                    />
                  </div>
                  <div>
                    <label className="text-xs text-gray-500 block mb-1">Phone</label>
                    <input
                      className="border rounded px-3 py-2 w-full text-sm"
                      value={editForm.phone}
                      onChange={e => setEditForm({...editForm, phone: e.target.value})}
                      placeholder="Phone"
                      data-testid="edit-phone"
                    />
                  </div>
                  <div>
                    <label className="text-xs text-gray-500 block mb-1">Agreement Amount ($)</label>
                    <input
                      type="number"
                      className="border rounded px-3 py-2 w-full text-sm"
                      value={editForm.agreement_amount}
                      onChange={e => setEditForm({...editForm, agreement_amount: parseFloat(e.target.value) || ''})}
                      placeholder="0.00"
                      data-testid="edit-agreement-amount"
                    />
                  </div>
                </div>
              ) : (
                <div className="grid grid-cols-2 gap-4">
                  <div className="flex items-center gap-3">
                    <Mail className="w-5 h-5 text-gray-400" />
                    <div>
                      <p className="text-xs text-gray-500">Email</p>
                      <a href={`mailto:${selectedLead.email}`} className="text-blue-600 hover:underline">
                        {selectedLead.email}
                      </a>
                    </div>
                  </div>
                  {selectedLead.phone && (
                    <div className="flex items-center gap-3">
                      <Phone className="w-5 h-5 text-gray-400" />
                      <div>
                        <p className="text-xs text-gray-500">Phone</p>
                        <a href={`tel:${selectedLead.phone}`} className="text-blue-600 hover:underline">
                          {selectedLead.phone}
                        </a>
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Estimate Value */}
              <div className="bg-green-50 rounded-lg p-4">
                <p className="text-sm text-green-700 mb-1">Estimated Value</p>
                <p className="text-3xl font-bold text-green-600">
                  {formatCurrency(selectedLead.estimated_mid || selectedLead.pricing?.total)}
                </p>
                {selectedLead.estimated_low && (
                  <p className="text-sm text-green-600">
                    Range: {formatCurrency(selectedLead.estimated_low)} - {formatCurrency(selectedLead.estimated_high)}
                  </p>
                )}
              </div>

              {/* Project Details */}
              <div className="grid grid-cols-2 gap-4">
                {selectedLead.financing_interest && (
                  <div>
                    <p className="text-xs text-gray-500">Financing</p>
                    <p className="font-medium capitalize">
                      {selectedLead.financing_interest.replace('_', ' ')}
                    </p>
                  </div>
                )}
                {selectedLead.project_timeline && (
                  <div>
                    <p className="text-xs text-gray-500">Timeline</p>
                    <p className="font-medium capitalize">
                      {selectedLead.project_timeline.replace('_', ' ')}
                    </p>
                  </div>
                )}
                {selectedLead.roof_area_sqft && (
                  <div>
                    <p className="text-xs text-gray-500">Roof Size</p>
                    <p className="font-medium">{selectedLead.roof_area_sqft.toLocaleString()} sq ft</p>
                  </div>
                )}
                {selectedLead.desired_roof_type && (
                  <div>
                    <p className="text-xs text-gray-500">Desired Material</p>
                    <p className="font-medium capitalize">{selectedLead.desired_roof_type}</p>
                  </div>
                )}
              </div>

              {/* Lead Source Selector */}
              <div>
                <p className="text-sm font-medium text-gray-700 mb-2">Lead Source</p>
                <select
                  value={selectedLead.source || ''}
                  onChange={(e) => {
                    updateLeadSource(selectedLead.id, e.target.value);
                    setSelectedLead({ ...selectedLead, source: e.target.value });
                  }}
                  className={`w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 ${getSourceColor(selectedLead.source, leadSources)}`}
                  data-testid="modal-source-select"
                >
                  <option value="">No Source</option>
                  {leadSources.map(source => (
                    <option key={source.id} value={source.id}>{source.label}</option>
                  ))}
                </select>
              </div>

              {/* Check-in / Follow-up Date */}
              <div>
                <p className="text-sm font-medium text-gray-700 mb-2 flex items-center gap-2">
                  <CalendarClock className="w-4 h-4 text-sky-600" />
                  Check-in Date
                </p>
                <p className="text-xs text-gray-500 mb-2">Set a date to follow up with this contact</p>
                <div className="flex items-center gap-2">
                  <input
                    type="date"
                    value={selectedLead.check_in_date ? selectedLead.check_in_date.split('T')[0] : ''}
                    onChange={(e) => {
                      const date = e.target.value ? new Date(e.target.value).toISOString() : null;
                      updateCheckInDate(selectedLead.id, date);
                      setSelectedLead({ ...selectedLead, check_in_date: date });
                    }}
                    className="flex-1 px-3 py-2 text-sm border border-gray-200 rounded-lg focus:ring-2 focus:ring-sky-500"
                    data-testid="modal-check-in-date"
                  />
                  {selectedLead.check_in_date && (
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => {
                        updateCheckInDate(selectedLead.id, null);
                        setSelectedLead({ ...selectedLead, check_in_date: null });
                      }}
                      className="text-gray-400 hover:text-red-500"
                    >
                      <X className="w-4 h-4" />
                    </Button>
                  )}
                </div>
                {selectedLead.check_in_date && new Date(selectedLead.check_in_date) <= new Date() && (
                  <p className="text-xs text-red-500 mt-1 font-medium">This check-in is overdue!</p>
                )}
              </div>

              {/* Stage Selector */}
              <div>
                <p className="text-sm font-medium text-gray-700 mb-2">Update Stage</p>
                <div className="flex flex-wrap gap-2">
                  {stages.map((stage) => (
                    <button
                      key={stage.id}
                      onClick={() => {
                        updateLeadStage(selectedLead.id, stage.id);
                        setSelectedLead({ ...selectedLead, stage: stage.id });
                      }}
                      className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                        selectedLead.stage === stage.id
                          ? 'text-white'
                          : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                      }`}
                      style={selectedLead.stage === stage.id ? { backgroundColor: stage.color } : {}}
                      data-testid={`modal-stage-${stage.id}`}
                    >
                      {stage.label}
                    </button>
                  ))}
                </div>
              </div>

              {/* Admin: Reassign Lead */}
              {isAdmin && (
                <div className="bg-purple-50 rounded-lg p-4">
                  <p className="text-sm font-medium text-purple-700 mb-2 flex items-center gap-2">
                    <RefreshCw className="w-4 h-4" />
                    Reassign Lead
                  </p>
                  {selectedLead.owner_name && (
                    <p className="text-xs text-purple-600 mb-2">
                      Currently assigned to: <strong>{selectedLead.owner_name}</strong>
                    </p>
                  )}
                  <div className="flex items-center gap-2">
                    <select
                      className="flex-1 border rounded-md px-3 py-2 text-sm"
                      data-testid="reassign-user-select"
                      defaultValue=""
                      onChange={(e) => {
                        if (e.target.value) {
                          reassignLead(selectedLead.id, e.target.value);
                        }
                      }}
                    >
                      <option value="">Select team member...</option>
                      {teamUsers.map(user => (
                        <option key={user.id} value={user.id}>
                          {user.name} {user.is_admin ? '(Admin)' : ''}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
              )}

              {/* Notes */}
              {selectedLead.notes && (
                <div>
                  <p className="text-xs text-gray-500 mb-1">Notes from Homeowner</p>
                  <p className="text-gray-700 bg-gray-50 rounded p-3">{selectedLead.notes}</p>
                </div>
              )}

              {/* Contractor Notes - With Audio Recording */}
              <div>
                <p className="text-sm font-medium text-gray-700 mb-2 flex items-center gap-2">
                  <StickyNote className="w-4 h-4 text-yellow-600" />
                  Contractor Notes
                  <span className="text-xs text-gray-400 font-normal flex items-center gap-1">
                    <Mic className="w-3 h-3" />
                    Voice or text
                  </span>
                </p>
                <AudioNoteRecorder
                  existingText={selectedLead.contractor_notes || ''}
                  onTranscriptionComplete={(text) => {
                    setSelectedLead({ ...selectedLead, contractor_notes: text });
                  }}
                />
                <Button
                  size="sm"
                  className="mt-2 bg-yellow-500 hover:bg-yellow-600"
                  onClick={() => saveContractorNotes(selectedLead.id, selectedLead.contractor_notes || '')}
                  data-testid="save-contractor-notes"
                >
                  <Save className="w-4 h-4 mr-1" />
                  Save Notes
                </Button>
              </div>
              {/* Files & Photos */}
              <div>
                <p className="text-sm font-medium text-gray-700 mb-3 flex items-center gap-2">
                  <FileText className="w-4 h-4 text-blue-600" />
                  Files & Photos
                  <span className="text-xs text-gray-400 font-normal">({leadFiles.length})</span>
                </p>
                
                {/* Upload controls */}
                <div className="flex items-center gap-2 mb-3">
                  <select
                    value={uploadCategory}
                    onChange={e => setUploadCategory(e.target.value)}
                    className="border rounded px-2 py-1.5 text-sm"
                    data-testid="file-category-select"
                  >
                    <option value="photo">Photo</option>
                    <option value="contract">Contract</option>
                    <option value="document">Document</option>
                    <option value="fieldcam">FieldCam Recording</option>
                    <option value="general">General</option>
                  </select>
                  <input
                    ref={fileInputRef}
                    type="file"
                    multiple
                    accept="image/*,video/*,.pdf,.doc,.docx,.xls,.xlsx,.txt,.csv"
                    onChange={handleFileUpload}
                    className="hidden"
                    data-testid="file-upload-input"
                  />
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => fileInputRef.current?.click()}
                    disabled={isUploadingFile}
                    data-testid="upload-file-btn"
                  >
                    <Plus className="w-4 h-4 mr-1" />
                    {isUploadingFile ? 'Uploading...' : 'Upload'}
                  </Button>
                </div>
                
                {/* Photos grid */}
                {leadFiles.filter(f => f.file_type === 'image').length > 0 && (
                  <div className="mb-3">
                    <p className="text-xs text-gray-500 mb-2">Photos <span className="text-gray-400">(tap to enlarge)</span></p>
                    <div className="grid grid-cols-4 gap-2">
                      {leadFiles.filter(f => f.file_type === 'image').map((file, idx) => (
                        <div key={file.id} className="relative group rounded-lg overflow-hidden border bg-gray-50" data-testid={`photo-${file.id}`}>
                          <img
                            src={file.url || `${BACKEND_URL}/api/uploads/lead_files/${file.lead_id}/${file.stored_filename}`}
                            alt={file.filename}
                            className="w-full h-24 object-cover cursor-pointer"
                            onClick={() => {
                              const images = leadFiles.filter(f => f.file_type === 'image').map(f => ({
                                url: f.url || `${BACKEND_URL}/api/uploads/lead_files/${f.lead_id}/${f.stored_filename}`,
                                filename: f.filename
                              }));
                              setLightboxImages(images);
                              setLightboxIndex(idx);
                            }}
                            onError={(e) => {
                              e.target.onerror = null;
                              e.target.src = 'data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" width="100" height="100"><rect fill="%23f3f4f6" width="100" height="100"/><text x="50%" y="50%" dominant-baseline="middle" text-anchor="middle" fill="%239ca3af" font-size="12">No Image</text></svg>';
                            }}
                          />
                          <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-40 transition-all flex items-center justify-center pointer-events-none">
                            <span className="opacity-0 group-hover:opacity-100 text-white text-xs">Click to enlarge</span>
                          </div>
                          <div className="absolute top-1 right-1">
                            <button
                              onClick={(e) => { e.stopPropagation(); deleteLeadFile(file.id); }}
                              className="opacity-0 group-hover:opacity-100 bg-red-500 text-white rounded-full p-0.5"
                              title="Delete"
                            >
                              <X className="w-3 h-3" />
                            </button>
                          </div>
                          <p className="text-[10px] text-gray-500 truncate px-1 py-0.5">{file.filename}</p>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                
                {/* Documents list */}
                {leadFiles.filter(f => f.file_type !== 'image').length > 0 && (
                  <div>
                    <p className="text-xs text-gray-500 mb-2">Documents</p>
                    <div className="space-y-1">
                      {leadFiles.filter(f => f.file_type !== 'image').map(file => (
                        <div key={file.id} className="flex items-center justify-between bg-gray-50 rounded px-3 py-2 text-sm" data-testid={`doc-${file.id}`}>
                          <div className="flex items-center gap-2 min-w-0">
                            <FileText className="w-4 h-4 text-gray-400 shrink-0" />
                            <a
                              href={file.url || `${BACKEND_URL}/api/uploads/lead_files/${file.lead_id}/${file.stored_filename}`}
                              target="_blank"
                              rel="noreferrer"
                              className="text-blue-600 hover:underline truncate"
                            >
                              {file.filename}
                            </a>
                            <span className="text-xs text-gray-400 shrink-0 capitalize">{file.category}</span>
                          </div>
                          <button
                            onClick={() => deleteLeadFile(file.id)}
                            className="text-red-400 hover:text-red-600 shrink-0 ml-2"
                          >
                            <Trash2 className="w-3.5 h-3.5" />
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                
                {leadFiles.length === 0 && (
                  <p className="text-sm text-gray-400 italic">No files uploaded yet. Use the Upload button to add photos, contracts, or documents.</p>
                )}
              </div>
            </div>

            <div className="p-6 border-t bg-gray-50 flex gap-3 flex-wrap">
              <Button 
                onClick={() => setSelectedLead(null)}
                variant="outline"
                className="flex-1 min-w-[100px]"
              >
                Close
              </Button>
              <Button 
                onClick={() => navigate(`/dashboard/estimates/${selectedLead.estimate_id || selectedLead.id}`)}
                className="flex-1 min-w-[100px] bg-green-600 hover:bg-green-700"
                data-testid="view-full-estimate"
              >
                <FileText className="w-4 h-4 mr-1" />
                View Estimate
              </Button>
              <Button 
                onClick={async () => {
                  try {
                    const token = localStorage.getItem('rooftrack_token');
                    const res = await axios.get(`${BACKEND_URL}/api/crm/leads/${selectedLead.id}/linked-data`, {
                      headers: { Authorization: `Bearer ${token}` }
                    });
                    if (res.data.claim_id) {
                      navigate(`/dashboard/claims/${res.data.claim_id}`);
                    } else {
                      startInsuranceClaim(selectedLead);
                    }
                  } catch {
                    startInsuranceClaim(selectedLead);
                  }
                }}
                className="flex-1 min-w-[100px] bg-orange-500 hover:bg-orange-600"
                data-testid="view-linked-claim"
              >
                <Shield className="w-4 h-4 mr-1" />
                Start/Open Claim
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Add Lead Modal */}
      {showAddLeadModal && <AddLeadModal />}

      {/* Image Lightbox */}
      {lightboxImages.length > 0 && (
        <ImageLightbox
          images={lightboxImages}
          initialIndex={lightboxIndex}
          onClose={() => setLightboxImages([])}
        />
      )}
    </div>
  );
};

export default LeadCRM;
